import { environment } from '@environments/environment';
import { AwardedEstimateService } from '@http/clients/reporting-app/awarded-estimates.service';
import { TabNames } from '@models/enums/TabNames';
import { GridDisplayScheme, OverviewLayout } from '@pages/reports/types/report-layout-type.enum';
import { WidgetType } from '@pages/reports/types/report-widget-type.enum';
import { formatNumbersCompact, percentageFormatter, truncateValue } from '@utils/format-functions';
import { AgChartLegendLabelFormatterParams } from 'ag-charts-community';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { IReportConfiguration } from '../../types/report-config.interface';
import { ReportType } from '../../types/report-type.enum';
import {
    accountEditorConfig,
    awardedAmountEditorConfig,
    awardedDateEditorConfig,
    customerEditorConfig,
    departmentEditorConfig,
    estimateTypeEditorConfig,
    includeArchivedEditorConfig,
    marketsEditorConfig,
    opportunitySourceEditorConfig,
    opportunityStatusEditorConfig,
    probabilityEditorConfig
} from './shared/additional-filters';
import { crmMainFilters } from './shared/main-filters';

export const EstimatesAwarded: IReportConfiguration<AwardedEstimateService> = {
    base: {
        type: ReportType.EstimateAwarded,
        serviceType: AwardedEstimateService
    },
    overviewTab: {
        widgets: [
            {
                methodName: 'getEstimateBoxesValues',
                type: WidgetType.Box,
                layoutTemplateArea: 'boxes'
            },
            {
                methodName: 'getEstimateMetricsValues',
                type: WidgetType.Metrics,
                layoutTemplateArea: 'metrics',
                settings: {
                    cols: [
                        { name: 'avg', label: 'Average' },
                        { name: 'min', label: 'Min' },
                        { name: 'med', label: 'Med' },
                        { name: 'max', label: 'Max' }
                    ],
                    rows: [
                        { name: 'AwardedAmount', label: 'Awarded Amount', format: 'currency' },
                        { name: 'GrossMarginPercentage', label: 'Gross Margin (%)', format: 'customPercent' }
                    ]
                }
            },
            {
                methodName: 'getEstimateLineChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart',
                settings: {
                    title: {
                        text: 'Top 10 Estimates by Awarded Amount'
                    },
                    series: [
                        {
                            type: 'line',
                            xKey: 'estimateName',
                            yKey: 'totalAwardedAmount',
                            yName: 'Awarded Amount'
                        },
                        {
                            type: 'line',
                            xKey: 'estimateName',
                            yKey: 'totalGrossMarginPercentage',
                            yName: 'Gross Margin (%)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            title: {
                                text: 'Amount'
                            },
                            keys: ['totalAwardedAmount'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            title: {
                                text: 'Percentage'
                            },
                            keys: ['totalGrossMarginPercentage'],
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    percentageFormatter(Number(params.value))
                            }
                        }
                    ]
                }
            },
            {
                methodName: 'getEstimateColumnChartValues',
                type: WidgetType.Chart,
                layoutTemplateArea: 'chart2',
                settings: {
                    title: {
                        text: 'Salesperson Performance'
                    },
                    series: [
                        {
                            type: 'bar',
                            xKey: 'salesPerson',
                            yKey: 'totalAwardedAmount',
                            yName: 'Awarded Amount'
                        },
                        {
                            type: 'line',
                            xKey: 'salesPerson',
                            yKey: 'totalGrossMarginPercentage',
                            yName: 'Gross Margin (%)'
                        }
                    ],
                    axes: [
                        {
                            type: 'category',
                            position: 'bottom',
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    truncateValue(params.value, 15),
                                rotation: 315
                            }
                        },
                        {
                            type: 'number',
                            position: 'left',
                            keys: ['totalAwardedAmount'],
                            title: {
                                text: 'Amount'
                            },
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    formatNumbersCompact(params.value)
                            }
                        },
                        {
                            type: 'number',
                            position: 'right',
                            keys: ['totalGrossMarginPercentage'],
                            title: {
                                text: 'Percentage'
                            },
                            label: {
                                formatter: (params: AgChartLegendLabelFormatterParams) =>
                                    percentageFormatter(Number(params.value))
                            }
                        }
                    ]
                }
            }
        ],
        layout: OverviewLayout.FourRowStack
    },
    gridTab: {
        widgets: [
            {
                methodName: 'getEstimateValues',
                type: WidgetType.Grid,
                layoutTemplateArea: 'grid',
                settings: [
                    {
                        field: 'baseEstimateId',
                        headerName: 'BID',
                        lockVisible: true,
                        cellRenderer: (params: ICellRendererParams) => {
                            if (!params.value) {
                                return;
                            }
                            if (params.data?.projectArchived) {
                                return params.value;
                            } else {
                                const link = `${environment.SalesAppClientBaseUrl}/${environment.SalesAppClientProjectOverviewUrl}/${params.value}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                        }
                    },
                    { field: 'estimateName', headerName: 'Estimate Name', enableRowGroup: true },
                    { field: 'estimateType', headerName: 'Estimate Type', enableRowGroup: true },
                    { field: 'awardedDate', headerName: 'Awarded Date', type: 'date' },
                    {
                        field: 'oid',
                        headerName: 'OID',
                        cellRenderer: (params: ICellRendererParams) => {
                            if (params.value) {
                                const link = `/opportunities-single/${params.value}/${TabNames[TabNames.Overview]}`;
                                return `<a class="anchor" href="${link}" target="_blank" rel="noopener noreferrer">${params.value}</a>`;
                            }
                            return '';
                        }
                    },
                    { field: 'opportunityName', headerName: 'Opportunity Name', enableRowGroup: true },
                    { field: 'customer', headerName: 'Customer', enableRowGroup: true },
                    { field: 'account', headerName: 'Account', enableRowGroup: true },
                    { field: 'awardedAmount', headerName: 'Awarded Amount', type: 'currency', aggFunc: 'sum' },
                    {
                        field: 'grossMarginPercentage',
                        headerName: 'Gross Margin (%)',
                        type: 'customPercentage',
                        aggFunc: 'avg'
                    },
                    { field: 'division', headerName: 'Department', enableRowGroup: true },
                    { field: 'salesPerson', headerName: 'Salesperson', enableRowGroup: true },
                    { field: 'status', headerName: 'Opportunity Status', enableRowGroup: true },
                    { field: 'market', headerName: 'Market' },
                    { field: 'leadSource', headerName: 'Opportunity Source', enableRowGroup: true },
                    { field: 'organization', headerName: 'Organization', enableRowGroup: true },
                    { field: 'probability', headerName: 'Probability', type: 'percentageString', aggFunc: 'avg' },
                    { field: 'condition', headerName: 'Condition', enableRowGroup: true },
                    {
                        field: 'opportunityLastUpdatedNote',
                        headerName: 'Last Updated Note',
                        tooltipField: 'opportunityLastUpdatedNote'
                    },
                    { field: 'dueDate', headerName: 'Due Date', type: 'date' },
                    { field: 'creationDate', headerName: 'Creation Date', type: 'date' },
                    { field: 'expectedBookingDate', headerName: 'Expected Booking Date', type: 'date' },
                    { field: 'companyNames', headerName: 'Companies', enableRowGroup: true },
                    { field: 'companyRoleNames', headerName: 'Company Roles', enableRowGroup: true },
                    {
                        field: 'actualValue',
                        headerName: 'Actual Value ($)',
                        type: 'currency',
                        enableValue: false
                    },
                    { field: 'avgTotalTerm', headerName: 'Term Average', type: 'currency', enableValue: false },
                    {
                        field: 'totalTerm',
                        headerName: '# of Terms',
                        enableValue: false,
                        valueFormatter: (params: any) => {
                            if (!params.value) return null;
                            const value = Number(params.value);
                            return Number.isInteger(value) ? value : value.toFixed(1);
                        }
                    },
                    {
                        field: 'actualGRossMarginPercentage',
                        headerName: 'Actual GM (%)',
                        type: 'customPercentage',
                        enableValue: false
                    },
                    { field: 'projectID', headerName: 'Project ID' },
                    { field: 'buildingName', headerName: 'Main Building Name', enableRowGroup: true },
                    {
                        field: 'potentialValue',
                        headerName: 'Potential Value ($)',
                        type: 'currency',
                        enableValue: false
                    },
                    {
                        field: 'proposedGrossMarginPercent',
                        headerName: 'Potential GM (%)',
                        type: 'customPercentage',
                        enableValue: false
                    },
                    {
                        field: 'potentialTermAverage',
                        headerName: 'Potential Term Average',
                        type: 'currency',
                        enableValue: false
                    },
                    {
                        field: 'potentialNumberOfTerms',
                        headerName: 'Potential # of Terms',
                        enableValue: false,
                        valueFormatter: (params: any) => {
                            if (!params.value) return null;
                            const value = Number(params.value);
                            return Number.isInteger(value) ? value : value.toFixed(1);
                        }
                    },
                    {
                        field: 'speculativeValue',
                        headerName: 'Speculative Value',
                        type: 'currency',
                        enableValue: false
                    },
                    { field: 'totalBuildingSqFt', headerName: 'Total Building Sq. Ft.', enableValue: false },
                    { field: 'sellPriceSqFt', headerName: 'Sell Price / Sq. Ft.', enableValue: false },
                    { field: 'productTypes', headerName: 'Product Types', enableRowGroup: true },
                    {
                        field: 'projectDuration',
                        headerName: 'Project Duration (days)',
                        enableRowGroup: true,
                        enableValue: false
                    },
                    { field: 'competingBidder', headerName: 'Competing Bidder', enableRowGroup: true },
                    { field: 'bidCategories', headerName: 'Bid Categories', enableRowGroup: true },
                    { field: 'modificationDate', headerName: 'Modification Date', type: 'date' }
                ]
            }
        ],
        layout: GridDisplayScheme.Standard
    },
    filters: {
        main: [...crmMainFilters],
        additional: [
            awardedDateEditorConfig,
            awardedAmountEditorConfig,
            estimateTypeEditorConfig,
            opportunityStatusEditorConfig,
            marketsEditorConfig,
            opportunitySourceEditorConfig,
            departmentEditorConfig,
            customerEditorConfig,
            accountEditorConfig,
            probabilityEditorConfig,
            includeArchivedEditorConfig
        ]
    }
};
